import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { DimensionRadar, DIMENSIONS } from "./DimensionRadar";
import { track } from "../lib/analytics";
import { useContent } from "../context/ContentContext";

export const DiscoveryFeature = () => {
  const heading = useContent(
    "discovery_teaser_heading",
    "Your Wellness Journey Begins with Awareness"
  );
  return (
  <section
    id="wellness-discovery-feature"
    data-testid="discovery-feature-section"
    className="relative overflow-hidden py-24 sm:py-32"
  >
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -left-40 top-24 h-[460px] w-[460px] rounded-full bg-sage/70 blur-3xl"
    />
    <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
      <div className="lg:col-span-6">
        <Reveal>
          <Overline>Introducing the Rooted Wellness Discovery</Overline>
          <h2 className="font-serif text-3xl leading-tight text-forest sm:text-4xl lg:text-5xl">
            {heading.split(" ").slice(0, -1).join(" ")}{" "}
            <em className="italic text-gold">{heading.split(" ").slice(-1)}</em>
          </h2>
          <p className="mt-7 text-lg font-light leading-relaxed text-charcoal/75">
            Your body doesn't exist separately from the life you're living. Explore how your
            physical habits, personal history, environment, emotional well-being, and spiritual
            connection fit into your unique wellness experience.
          </p>
          <p className="mt-5 leading-relaxed text-charcoal/70">
            Discover what's supporting you, what deserves more attention, and where your next
            chapter might begin. Create your free account, explore your personalized wellness
            profile, and return to track your journey over time.
          </p>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="mt-9 flex flex-wrap items-center gap-5">
            <Link
              to="/wellness-discovery"
              data-testid="discovery-feature-cta"
              onClick={() => track("discovery_cta_click", { location: "homepage-feature" })}
              className="group inline-flex items-center gap-2 rounded-full bg-forest px-8 py-4 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-forest-deep hover:shadow-[0_14px_35px_rgba(23,63,53,0.28)] hover:-translate-y-0.5"
            >
              Discover My Wellness Profile
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
          <p className="mt-4 text-sm text-taupe">Free to join. Member accounts open soon.</p>
        </Reveal>
      </div>
      <Reveal className="lg:col-span-6" delay={0.15}>
        <div className="relative mx-auto max-w-md rounded-[2.5rem] border border-forest/10 bg-white/70 p-7 shadow-[0_25px_60px_rgba(23,63,53,0.12)] backdrop-blur-sm sm:p-9">
          <DimensionRadar values={[76, 64, 70, 58, 84]} />
          <ul className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {DIMENSIONS.map((d) => (
              <li key={d.key} className="flex items-center gap-2.5 text-xs text-charcoal/70">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-gold" aria-hidden="true" />
                {d.name}
              </li>
            ))}
          </ul>
          <p className="mt-5 border-t border-forest/10 pt-4 text-[11px] uppercase tracking-[0.2em] text-taupe">
            Illustrative preview · Demonstration data
          </p>
        </div>
      </Reveal>
    </div>
  </section>
  );
};
